import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination, Transition } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
import { getBranchList, getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import { getUser } from "helpers/auth-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"

export default function OfflineReturnList() {
  const { can, currentBranch, canFilterAllOutlets } = useAuth()
  const { title, transition, setTransition } = useOutletContext()
  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  // 240726 (permintaan user): default filter "ikut outlet user" (grup HO/Semua Outlet).
  const rawOutletId = searchParams.get("outlet_id") ?? getDefaultOutletFilterValue()
  const outletFilter = isBranchLocked ? String(currentBranch.id) : rawOutletId
  const { outlet_id: resolvedOutletId, branch_list: resolvedBranchList } = resolveOutletFilter(outletFilter)
  const dateFrom = searchParams.get("date_from") ?? ""
  const dateTo = searchParams.get("date_to") ?? ""
  const searchParam = searchParams.get("search") ?? ""

  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)
  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()
  const branchNameById = useMemo(() => {
    const map = {}
    ;(branchSelection || []).forEach((x) => {
      if (x?.id != null) map[String(x.id)] = x.name
    })
    return map
  }, [branchSelection])

  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `offline-returns`,
    {
      // Grup → branch_list (kumpulan id); outlet individual → outlet_id tunggal.
      outlet_id: resolvedOutletId || undefined,
      branch_list: JSON.stringify(resolvedBranchList),
      page,
      record_per_page: 10,
      search: searchParam || undefined,
      status: status || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }
  )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => { if (searchInput.current) searchInput.current.focus() }, [])
  useEffect(() => { if (isLoading) nProgress.start(); else nProgress.done() }, [isLoading])

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })
    if (patch && ["search", "status", "outlet_id", "date_from", "date_to"].some((k) => Object.prototype.hasOwnProperty.call(patch, k))) {
      next.delete("page")
    }
    setSearchParams(next, opts)
  }

  const onSearchSubmit = () => patchSearchParams({ search, page: 1 })

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (outletFilter) tmp += `&outlet_id=${outletFilter}`
    if (dateFrom) tmp += `&date_from=${dateFrom}`
    if (dateTo) tmp += `&date_to=${dateTo}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data.data
    return []
  }, [result_set])

  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium"
    if (s === "POSTED") return <span className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "DRAFT") return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED") return <span className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  const formatDate = (value) => {
    if (!value) return "-"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "long", year: "numeric" }).format(d)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    const userId = getUser()?.id
    const payload = userId ? { user_id: userId, ...(body || {}) } : body
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload ? JSON.stringify(payload) : null,
    })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok || json?.success === 0) {
      const flat = json?.errors && typeof json.errors === "object" ? Object.values(json.errors).flat().filter(Boolean) : []
      throw new Error(flat.length ? String(flat[0]) : (json?.message || "Permintaan gagal"))
    }
    return json
  }

  const [actingId, setActingId] = useState(null)

  const onPost = async (ret) => {
    if (!ret?.id) return
    if (!window.confirm(`Posting retur ${ret.return_number || ""}? Stok akan MASUK ke outlet tujuan.`)) return
    try {
      setActingId(ret.id)
      await postJson(`/offline-returns/${ret.id}/post`)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal posting retur")
    } finally {
      setActingId(null)
    }
  }

  const onCancel = async (ret) => {
    if (!ret?.id) return
    const reason = window.prompt(`Batalkan retur ${ret.return_number || ""}\nAlasan (opsional):`, "")
    if (reason === null) return
    try {
      setActingId(ret.id)
      await postJson(`/offline-returns/${ret.id}/cancel`, reason ? { reason } : null)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal membatalkan retur")
    } finally {
      setActingId(null)
    }
  }

  const isDraft = (ret) => String(ret?.status || "").toUpperCase() === "DRAFT"

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>
              <input
                ref={searchInput}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSearchSubmit() } }}
                value={search}
                type="text"
                placeholder={`${lang.search} Retur Toko Offline`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            <OutletFilterSelect
              value={outletFilter}
              onChange={(v) => patchSearchParams({ outlet_id: v })}
              disabled={isBranchLocked}
              title="Outlet Tujuan"
              className="hidden px-3 py-3 text-xs transition border sm:block border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
            />

            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden px-3 py-3 text-xs transition border sm:block border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">Semua Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="POSTED">POSTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => patchSearchParams({ date_from: e.target.value })}
              className="hidden px-3 py-3 text-xs transition border sm:block border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              title="Tanggal Dari"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => patchSearchParams({ date_to: e.target.value })}
              className="hidden px-3 py-3 text-xs transition border sm:block border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              title="Tanggal Sampai"
            />
          </div>

          <div className="flex items-center space-x-2">
            {can("offline-return.create") && (
              <Link
                to="create"
                onClick={() => setTransition({ type: "slide", reverse: true })}
                state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
              >
                <Plus className="w-4 h-4" stroke={1.5} />
                <span>{lang.create}</span>
              </Link>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">No. Retur</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Toko Offline (Asal)</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">HO Atalla (Tujuan)</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tanggal</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Item</th>
                <th className="relative px-3 py-3"><span className="sr-only">{lang.action}</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={7} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">{lang.loading_data}</td>
                </tr>
              )}
              {!isLoading && list.length === 0 && (
                <tr className="text-center">
                  <td colSpan={7} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {searchParam ? lang.no_result : lang.no_data}
                  </td>
                </tr>
              )}
              {!isLoading && list.map((ret) => (
                <tr key={ret.id} className="transition-colors hover:bg-brand-50">
                  <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">{ret.return_number ?? "-"}</td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{branchNameById[String(ret.source_outlet_id)] ?? ret.source_outlet_id ?? "-"}</td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{branchNameById[String(ret.dest_outlet_id)] ?? ret.dest_outlet_id ?? "-"}</td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{formatDate(ret.return_date)}</td>
                  <td className="px-3 py-4 text-xs whitespace-nowrap">{statusBadge(ret.status)}</td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{ret.items_count ?? "-"}</td>
                  <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const base = process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")
                          window.open(`${base}/offline-returns/${ret.id}/print`, "_blank")
                        }}
                        className="px-3 py-2 text-[11px] font-semibold text-sky-700 border border-sky-200 rounded-lg hover:bg-sky-50"
                      >
                        Print
                      </button>
                      {(can("offline-return.update") || can("offline-return.view")) && (
                        <Link
                          to={`${ret.id}/edit`}
                          state={{ back: title, from: location?.pathname, transition: "slide" }}
                          className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
                        >
                          <Pencil className="w-6 h-6" />
                        </Link>
                      )}
                      {can("offline-return.post") && isDraft(ret) && (
                        <button
                          type="button"
                          disabled={actingId === ret.id}
                          onClick={() => onPost(ret)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-green-700 rounded-lg hover:bg-green-600 disabled:opacity-60"
                        >
                          {actingId === ret.id ? "..." : "Posting"}
                        </button>
                      )}
                      {can("offline-return.cancel") && isDraft(ret) && (
                        <button
                          type="button"
                          disabled={actingId === ret.id}
                          onClick={() => onCancel(ret)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                        >
                          {actingId === ret.id ? "..." : "Batal"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination params={generatePaginationParam()} links={paginationLinks} from={from} to={to} total={total} />
      </div>
    </Transition>
  )
}
